import {
  Dismiss20Regular,
  Add20Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "./SearchableSelect";

// Interfaces
export interface SubempreiteiroExecucao {
  id: string;
  subempreiteiroId: string;
  descricao: string;
  valor: number;
}

export interface FornecedorExecucao {
  id: string;
  fornecedorId: string;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  valor: number;
}

export interface MaterialExecucao {
  id: string;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  valor: number;
}

export interface EquipaInterna {
  ativo: boolean;
  valor: number;
  notas: string;
}

export type ExecucaoCustoMode = "total" | "unitario";

export interface ExecucaoLinha {
  subempreiteiros: SubempreiteiroExecucao[];
  fornecedores: FornecedorExecucao[];
  materiais: MaterialExecucao[];
  equipaInterna: EquipaInterna;
  modoCusto?: ExecucaoCustoMode;
}

export interface ExecucaoOption {
  id: string;
  nome: string;
}

interface GerirExecucaoPanelProps {
  isOpen: boolean;
  onClose: () => void;
  linhaDescricao: string;
  execucaoAtual: ExecucaoLinha | null;
  onSalvar: (execucao: ExecucaoLinha) => void;
  subempreiteirosDisponiveis: ExecucaoOption[];
  fornecedoresDisponiveis: ExecucaoOption[];
  onCriarSubempreiteiro: (nome: string) => Promise<ExecucaoOption>;
  onCriarFornecedor: (nome: string) => Promise<ExecucaoOption>;
  isReadOnly?: boolean;
}

export function GerirExecucaoPanel({
  isOpen,
  onClose,
  linhaDescricao,
  execucaoAtual,
  onSalvar,
  subempreiteirosDisponiveis,
  fornecedoresDisponiveis,
  onCriarSubempreiteiro,
  onCriarFornecedor,
  isReadOnly = false,
}: GerirExecucaoPanelProps) {
  const isReadOnlyMode = isReadOnly;
  const [temSubempreiteiros, setTemSubempreiteiros] = useState(
    (execucaoAtual?.subempreiteiros?.length ?? 0) > 0
  );
  const [subempreiteiros, setSubempreiteiros] = useState<SubempreiteiroExecucao[]>(
    execucaoAtual?.subempreiteiros ?? []
  );
  const [fornecedores, setFornecedores] = useState<FornecedorExecucao[]>(
    execucaoAtual?.fornecedores ?? []
  );
  const [materiais, setMateriais] = useState<MaterialExecucao[]>(
    execucaoAtual?.materiais ?? []
  );
  const [equipaInterna, setEquipaInterna] = useState<EquipaInterna>(
    execucaoAtual?.equipaInterna ?? { ativo: false, valor: 0, notas: "" }
  );
  const [modoCusto, setModoCusto] = useState<ExecucaoCustoMode>(
    execucaoAtual?.modoCusto ?? "total"
  );
  const [novoSubempreiteiroLinhaId, setNovoSubempreiteiroLinhaId] = useState<string | null>(null);
  const [novoSubempreiteiroNome, setNovoSubempreiteiroNome] = useState("");
  const [novoFornecedorLinhaId, setNovoFornecedorLinhaId] = useState<string | null>(null);
  const [novoFornecedorNome, setNovoFornecedorNome] = useState("");
  const [isCriarSubempreiteiro, setIsCriarSubempreiteiro] = useState(false);
  const [isCriarFornecedor, setIsCriarFornecedor] = useState(false);

  // Entidades criadas aqui mesmo, pela criação rápida. Ficam à parte porque a lista
  // que vem por props só se actualiza quando o ecrã de cima refaz o seu estado: até
  // lá o seletor não saberia o nome do que acabou de ser criado e mostrava o id.
  const [criadosAgora, setCriadosAgora] = useState<
    SearchableSelectOption<string>[]
  >([]);

  const juntarCriados = useCallback(
    (base: SearchableSelectOption<string>[]) => {
      const conhecidos = new Set(base.map((option) => option.value));
      const novos = criadosAgora.filter(
        (option) => !conhecidos.has(option.value),
      );
      return novos.length > 0 ? [...novos, ...base] : base;
    },
    [criadosAgora],
  );

  const subempreiteiroOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      juntarCriados(
        subempreiteirosDisponiveis.map((s) => ({ value: s.id, label: s.nome })),
      ),
    [subempreiteirosDisponiveis, juntarCriados]
  );

  const fornecedorOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      juntarCriados(
        fornecedoresDisponiveis.map((f) => ({ value: f.id, label: f.nome })),
      ),
    [fornecedoresDisponiveis, juntarCriados]
  );

  const calcularValorMaterial = useCallback(
    (quantidade: number, valorUnitario: number): number => {
      if (!Number.isFinite(quantidade) || !Number.isFinite(valorUnitario)) {
        return 0;
      }
      return Number((quantidade * valorUnitario).toFixed(2));
    },
    []
  );

  const calcularValorFornecedor = useCallback(
    (quantidade: number, valorUnitario: number): number => {
      if (!Number.isFinite(quantidade) || !Number.isFinite(valorUnitario)) {
        return 0;
      }
      return Number((quantidade * valorUnitario).toFixed(2));
    },
    []
  );

  const normalizarFornecedores = useCallback(
    (lista: FornecedorExecucao[] | null | undefined): FornecedorExecucao[] => {
      return (lista ?? []).map((fornecedor) => {
        const quantidade = Number.isFinite(fornecedor.quantidade)
          ? fornecedor.quantidade
          : 0;
        const valorUnitario = Number.isFinite(fornecedor.valorUnitario)
          ? fornecedor.valorUnitario
          : 0;
        const valorCalculado = calcularValorFornecedor(quantidade, valorUnitario);
        const valor = valorCalculado > 0 ? valorCalculado : fornecedor.valor ?? 0;

        return {
          ...fornecedor,
          quantidade,
          valorUnitario,
          valor,
        };
      });
    },
    [calcularValorFornecedor]
  );

  const normalizarMateriais = useCallback(
    (lista: MaterialExecucao[] | null | undefined): MaterialExecucao[] => {
      return (lista ?? []).map((material) => {
        const quantidade = Number.isFinite(material.quantidade)
          ? material.quantidade
          : 0;
        const valorUnitario = Number.isFinite(material.valorUnitario)
          ? material.valorUnitario
          : 0;
        const valorCalculado = calcularValorMaterial(quantidade, valorUnitario);
        const valor = valorCalculado > 0 ? valorCalculado : material.valor ?? 0;

        return {
          ...material,
          quantidade,
          valorUnitario,
          valor,
        };
      });
    },
    [calcularValorMaterial]
  );

  const totalExecucao = useMemo(() => {
    const totalSub = subempreiteiros.reduce((acc, item) => acc + item.valor, 0);
    const totalForn = fornecedores.reduce((acc, item) => acc + item.valor, 0);
    const totalMateriais = materiais.reduce((acc, item) => acc + item.valor, 0);
    const totalInterno = equipaInterna.ativo ? equipaInterna.valor : 0;

    return Number((totalSub + totalForn + totalMateriais + totalInterno).toFixed(2));
  }, [subempreiteiros, fornecedores, materiais, equipaInterna]);

  const totalExecucaoFormatado = useMemo(() => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalExecucao);
  }, [totalExecucao]);

  // Reset estados quando o painel abre com nova execução
  useEffect(() => {
    if (isOpen && execucaoAtual !== null) {
      setTemSubempreiteiros((execucaoAtual?.subempreiteiros?.length ?? 0) > 0);
      setSubempreiteiros(execucaoAtual?.subempreiteiros ?? []);
      setFornecedores(normalizarFornecedores(execucaoAtual?.fornecedores));
      setMateriais(normalizarMateriais(execucaoAtual?.materiais));
      setEquipaInterna(execucaoAtual?.equipaInterna ?? { ativo: false, valor: 0, notas: "" });
      setModoCusto(execucaoAtual?.modoCusto ?? "total");
      setNovoSubempreiteiroLinhaId(null);
      setNovoSubempreiteiroNome("");
      setNovoFornecedorLinhaId(null);
      setNovoFornecedorNome("");
    } else if (isOpen && execucaoAtual === null) {
      // Reset para valores vazios quando não há execução prévia
      setTemSubempreiteiros(false);
      setSubempreiteiros([]);
      setFornecedores([]);
      setMateriais([]);
      setEquipaInterna({ ativo: false, valor: 0, notas: "" });
      setModoCusto("total");
      setNovoSubempreiteiroLinhaId(null);
      setNovoSubempreiteiroNome("");
      setNovoFornecedorLinhaId(null);
      setNovoFornecedorNome("");
    }
  }, [isOpen, execucaoAtual, normalizarFornecedores, normalizarMateriais]);

  const criarSubempreiteiroRapido = async (linhaId: string) => {
    const nome = novoSubempreiteiroNome.trim();
    if (!nome) {
      alert("Indique o nome do sub-empreiteiro.");
      return;
    }

    setIsCriarSubempreiteiro(true);
    try {
      const criado = await onCriarSubempreiteiro(nome);
      setCriadosAgora((prev) => [...prev, { value: criado.id, label: criado.nome }]);
      atualizarSubempreiteiro(linhaId, "subempreiteiroId", criado.id);
      setNovoSubempreiteiroLinhaId(null);
      setNovoSubempreiteiroNome("");
    } catch {
      alert("Não foi possível criar o sub-empreiteiro.");
    } finally {
      setIsCriarSubempreiteiro(false);
    }
  };

  const criarFornecedorRapido = async (linhaId: string) => {
    const nome = novoFornecedorNome.trim();
    if (!nome) {
      alert("Indique o nome do fornecedor.");
      return;
    }

    setIsCriarFornecedor(true);
    try {
      const criado = await onCriarFornecedor(nome);
      setCriadosAgora((prev) => [...prev, { value: criado.id, label: criado.nome }]);
      atualizarFornecedor(linhaId, "fornecedorId", criado.id);
      setNovoFornecedorLinhaId(null);
      setNovoFornecedorNome("");
    } catch {
      alert("Não foi possível criar o fornecedor.");
    } finally {
      setIsCriarFornecedor(false);
    }
  };

  const adicionarSubempreiteiro = () => {
    setSubempreiteiros([
      ...subempreiteiros,
      {
        id: `sub-exec-${Date.now()}`,
        subempreiteiroId: "",
        descricao: "",
        valor: 0,
      },
    ]);
  };

  const removerSubempreiteiro = (id: string) => {
    setSubempreiteiros(subempreiteiros.filter((s) => s.id !== id));
  };

  const atualizarSubempreiteiro = (
    id: string,
    campo: keyof SubempreiteiroExecucao,
    valor: any
  ) => {
    setSubempreiteiros(
      subempreiteiros.map((s) => (s.id === id ? { ...s, [campo]: valor } : s))
    );
  };

  const adicionarFornecedor = () => {
    setFornecedores([
      ...fornecedores,
      {
        id: `forn-exec-${Date.now()}`,
        fornecedorId: "",
        descricao: "",
        quantidade: 0,
        valorUnitario: 0,
        valor: 0,
      },
    ]);
  };

  const removerFornecedor = (id: string) => {
    setFornecedores(fornecedores.filter((f) => f.id !== id));
  };

  const atualizarFornecedor = (
    id: string,
    campo: keyof FornecedorExecucao,
    valor: any
  ) => {
    setFornecedores(
      fornecedores.map((f) => {
        if (f.id !== id) {
          return f;
        }

        const atualizado = { ...f, [campo]: valor };
        if (campo === "quantidade" || campo === "valorUnitario") {
          const quantidade = Number.isFinite(atualizado.quantidade)
            ? atualizado.quantidade
            : 0;
          const valorUnitario = Number.isFinite(atualizado.valorUnitario)
            ? atualizado.valorUnitario
            : 0;
          atualizado.valor = calcularValorFornecedor(quantidade, valorUnitario);
        }

        return atualizado;
      })
    );
  };

  const adicionarMaterial = () => {
    setMateriais([
      ...materiais,
      {
        id: `mat-exec-${Date.now()}`,
        descricao: "",
        quantidade: 0,
        valorUnitario: 0,
        valor: 0,
      },
    ]);
  };

  const removerMaterial = (id: string) => {
    setMateriais(materiais.filter((m) => m.id !== id));
  };

  const atualizarMaterial = (
    id: string,
    campo: keyof MaterialExecucao,
    valor: any
  ) => {
    setMateriais(
      materiais.map((m) => {
        if (m.id !== id) {
          return m;
        }

        const atualizado = { ...m, [campo]: valor };
        if (campo === "quantidade" || campo === "valorUnitario") {
          const quantidade = Number.isFinite(atualizado.quantidade)
            ? atualizado.quantidade
            : 0;
          const valorUnitario = Number.isFinite(atualizado.valorUnitario)
            ? atualizado.valorUnitario
            : 0;
          atualizado.valor = calcularValorMaterial(quantidade, valorUnitario);
        }

        return atualizado;
      })
    );
  };

  const handleSalvar = () => {
    if (isReadOnlyMode) {
      onClose();
      return;
    }
    // Validação básica
    if (temSubempreiteiros) {
      const subempreiteirosInvalidos = subempreiteiros.filter(
        s => !s.subempreiteiroId || s.valor === 0
      );
      if (subempreiteirosInvalidos.length > 0) {
        alert('Por favor, preencha todos os campos dos sub-empreiteiros (nome e valor).');
        return;
      }
    }

    const fornecedoresInvalidos = fornecedores.filter(
      f =>
        !f.fornecedorId ||
        !f.descricao.trim() ||
        f.quantidade <= 0 ||
        f.valorUnitario <= 0
    );
    if (fornecedoresInvalidos.length > 0) {
      alert('Por favor, preencha todos os campos dos fornecedores (nome e valor).');
      return;
    }

    const materiaisInvalidos = materiais.filter(
      m =>
        !m.descricao.trim() ||
        m.quantidade <= 0 ||
        m.valorUnitario <= 0
    );
    if (materiaisInvalidos.length > 0) {
      alert('Por favor, preencha todos os campos dos materiais (descrição, quantidade e valor unitário).');
      return;
    }

    if (equipaInterna.ativo && equipaInterna.valor === 0) {
      alert('Por favor, preencha o valor da equipa interna.');
      return;
    }

    const execucao: ExecucaoLinha = {
      subempreiteiros: temSubempreiteiros ? subempreiteiros : [],
      fornecedores,
      materiais,
      equipaInterna,
      modoCusto,
    };
    onSalvar(execucao);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full md:w-[600px] lg:w-[700px] bg-card border-l border-border shadow-lg z-50 overflow-y-auto animate-slide-in-right"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2
                className="text-foreground"
                style={{
                  fontSize: "var(--text-section-title)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Distribuição da Execução da Linha
              </h2>
              <p
                className="text-secondary-text mt-1"
                style={{ fontSize: "var(--text-caption)" }}
              >
                {linhaDescricao}
              </p>
            </div>
            <div className="p-2">
              <Dismiss20Regular className="w-5 h-5 text-foreground/50" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* SECÇÃO 0 — Aplicação ao custo unitário */}
          <div
            className="bg-background border border-border p-5 space-y-4"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <div>
              <h3
                className="text-foreground mb-1"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Aplicação ao custo unitário
              </h3>
              <p
                className="text-secondary-text"
                style={{ fontSize: "var(--text-caption)" }}
              >
                Total calculado da execução: {totalExecucaoFormatado}
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="execucaoModoCusto"
                  value="total"
                  checked={modoCusto === "total"}
                  disabled={isReadOnlyMode}
                  onChange={() => setModoCusto("total")}
                  className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                />
                <span
                  className="text-foreground"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  O total da execução é o total do item (divide pela quantidade do item).
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="execucaoModoCusto"
                  value="unitario"
                  checked={modoCusto === "unitario"}
                  disabled={isReadOnlyMode}
                  onChange={() => setModoCusto("unitario")}
                  className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                />
                <span
                  className="text-foreground"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  O total da execução é o custo unitário do item.
                </span>
              </label>
            </div>
          </div>

          {/* SECÇÃO A — Sub-empreiteiros */}
          <div
            className="bg-background border border-border p-5 space-y-4"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <div>
              <h3
                className="text-foreground mb-3"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Sub-empreiteiros
              </h3>

              {/* Checkbox */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={temSubempreiteiros}
                  disabled={isReadOnlyMode}
                  onChange={(e) => {
                    setTemSubempreiteiros(e.target.checked);
                    if (!e.target.checked) {
                      setSubempreiteiros([]);
                    }
                  }}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                  style={{ borderRadius: "var(--radius-sm)" }}
                />
                <span
                  className="text-foreground"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Esta linha é (também) executada por Sub-empreiteiros
                </span>
              </label>
            </div>

            {/* Tabela de Sub-empreiteiros */}
            {temSubempreiteiros && (
              <div className="space-y-3">
                {subempreiteiros.map((sub) => (
                  <div
                    key={sub.id}
                    className="grid grid-cols-12 gap-3 p-3 bg-card border border-border"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {/* Sub-empreiteiro */}
                    <div className="col-span-12 md:col-span-4">
                      <SearchableSelect<string>
                        label="Sub-empreiteiro"
                        labelClassName="block text-foreground mb-1"
                        value={sub.subempreiteiroId === "" ? null : sub.subempreiteiroId}
                        disabled={isReadOnlyMode}
                        onChange={(next) =>
                          atualizarSubempreiteiro(sub.id, "subempreiteiroId", next ?? "")
                        }
                        options={subempreiteiroOptions}
                        placeholder="Selecione..."
                        searchPlaceholder="Procurar sub-empreiteiro..."
                        panelPosition="fixed"
                      />
                      {!isReadOnlyMode && (
                        <div className="mt-2">
                          {novoSubempreiteiroLinhaId === sub.id ? (
                            <div className="flex flex-wrap gap-2">
                              <input
                                type="text"
                                value={novoSubempreiteiroNome}
                                onChange={(e) => setNovoSubempreiteiroNome(e.target.value)}
                                placeholder="Nome do sub-empreiteiro"
                                className="flex-1 min-w-[180px] px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                style={{
                                  borderRadius: "var(--radius-button)",
                                  fontSize: "var(--text-base)",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => void criarSubempreiteiroRapido(sub.id)}
                                disabled={isCriarSubempreiteiro}
                                className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-50"
                                style={{ borderRadius: "var(--radius-button)" }}
                              >
                                {isCriarSubempreiteiro ? "A criar..." : "Criar"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setNovoSubempreiteiroLinhaId(null);
                                  setNovoSubempreiteiroNome("");
                                }}
                                className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                                style={{ borderRadius: "var(--radius-button)" }}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setNovoSubempreiteiroLinhaId(sub.id);
                                setNovoSubempreiteiroNome("");
                              }}
                              className="text-primary hover:text-primary/80 transition-colors"
                              style={{ fontSize: "var(--text-caption)" }}
                            >
                              Criar sub-empreiteiro apenas com o nome
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Descrição */}
                    <div className="col-span-12 md:col-span-5">
                      <label
                        className="block text-foreground mb-1"
                        style={{
                          fontSize: "var(--text-caption)",
                          fontWeight: "var(--font-weight-semi-bold)",
                        }}
                      >
                        Descrição / Âmbito
                      </label>
                      <input
                        type="text"
                        value={sub.descricao}
                        disabled={isReadOnlyMode}
                        onChange={(e) =>
                          atualizarSubempreiteiro(sub.id, "descricao", e.target.value)
                        }
                        placeholder="Ex: Aplicação de pavimento"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                      />
                    </div>

                    {/* Valor */}
                    <div className="col-span-10 md:col-span-2">
                      <label
                        className="block text-foreground mb-1"
                        style={{
                          fontSize: "var(--text-caption)",
                          fontWeight: "var(--font-weight-semi-bold)",
                        }}
                      >
                        Valor (€)
                      </label>
                      <input
                        type="number"
                        value={sub.valor === 0 ? "" : sub.valor}
                        disabled={isReadOnlyMode}
                        onChange={(e) =>
                          atualizarSubempreiteiro(
                            sub.id,
                            "valor",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="0,00"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                        step="0.01"
                      />
                    </div>

                    {/* Remover */}
                    {!isReadOnlyMode && (
                      <div className="col-span-2 md:col-span-1 flex items-end justify-center">
                        <button
                          onClick={() => removerSubempreiteiro(sub.id)}
                          className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                          style={{ borderRadius: "var(--radius)" }}
                          title="Remover"
                        >
                          <Delete20Regular className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Botão Adicionar */}
                {!isReadOnlyMode && (
                  <button
                    onClick={adicionarSubempreiteiro}
                    className="w-full px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center justify-center gap-2"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                  >
                    <Add20Regular className="w-5 h-5" />
                    Adicionar Sub-empreiteiro
                  </button>
                )}
              </div>
            )}
          </div>

          {/* SECÇÃO B — Materiais & Equipa Interna */}
          <div
            className="bg-background border border-border p-5 space-y-5"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <h3
              className="text-foreground"
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Materiais & Equipa Interna
            </h3>

            {/* Materiais */}
            <div className="space-y-3">
              <h4
                className="text-foreground"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Materiais
              </h4>

              {materiais.map((material) => (
                <div
                  key={material.id}
                  className="grid grid-cols-12 gap-3 p-3 bg-card border border-border"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {/* Descrição */}
                  <div className="col-span-12 md:col-span-3">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={material.descricao}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarMaterial(material.id, "descricao", e.target.value)
                      }
                      placeholder="Ex: Argamassa, tinta, cabos"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  {/* Quantidade */}
                  <div className="col-span-6 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Quantidade
                    </label>
                    <input
                      type="number"
                      value={material.quantidade === 0 ? "" : material.quantidade}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarMaterial(
                          material.id,
                          "quantidade",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="0"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.001"
                    />
                  </div>

                  {/* Valor unitário */}
                  <div className="col-span-6 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Valor unit. (€)
                    </label>
                    <input
                      type="number"
                      value={material.valorUnitario === 0 ? "" : material.valorUnitario}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarMaterial(
                          material.id,
                          "valorUnitario",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Valor total */}
                  <div className="col-span-10 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Total (€)
                    </label>
                    <input
                      type="number"
                      value={material.valor === 0 ? "" : material.valor}
                      readOnly
                      disabled={isReadOnlyMode}
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Remover */}
                  {!isReadOnlyMode && (
                    <div className="col-span-2 md:col-span-1 flex items-end justify-center">
                      <button
                        onClick={() => removerMaterial(material.id)}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                        style={{ borderRadius: "var(--radius)" }}
                        title="Remover"
                      >
                        <Delete20Regular className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Botão Adicionar Material */}
              {!isReadOnlyMode && (
                <button
                  onClick={adicionarMaterial}
                  className="w-full px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center justify-center gap-2"
                  style={{
                    borderRadius: "var(--radius-button)",
                    fontSize: "var(--text-base)",
                  }}
                >
                  <Add20Regular className="w-5 h-5" />
                  Adicionar Material
                </button>
              )}
            </div>

            {/* Fornecedores */}
            <div className="space-y-3">
              <h4
                className="text-foreground"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Fornecedores
              </h4>

              {fornecedores.map((forn) => (
                <div
                  key={forn.id}
                  className="grid grid-cols-12 gap-3 p-3 bg-card border border-border"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {/* Fornecedor */}
                  <div className="col-span-12 md:col-span-4">
                    <SearchableSelect<string>
                      label="Fornecedor"
                      labelClassName="block text-foreground mb-1"
                      value={forn.fornecedorId === "" ? null : forn.fornecedorId}
                      disabled={isReadOnlyMode}
                      onChange={(next) =>
                        atualizarFornecedor(forn.id, "fornecedorId", next ?? "")
                      }
                      options={fornecedorOptions}
                      placeholder="Comece a escrever..."
                      searchPlaceholder="Comece a escrever..."
                      panelPosition="fixed"
                    />
                    {!isReadOnlyMode && (
                      <div className="mt-2">
                        {novoFornecedorLinhaId === forn.id ? (
                          <div className="flex flex-wrap gap-2">
                            <input
                              type="text"
                              value={novoFornecedorNome}
                              onChange={(e) => setNovoFornecedorNome(e.target.value)}
                              placeholder="Nome do fornecedor"
                              className="flex-1 min-w-[180px] px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                              style={{
                                borderRadius: "var(--radius-button)",
                                fontSize: "var(--text-base)",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => void criarFornecedorRapido(forn.id)}
                              disabled={isCriarFornecedor}
                              className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-50"
                              style={{ borderRadius: "var(--radius-button)" }}
                            >
                              {isCriarFornecedor ? "A criar..." : "Criar"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setNovoFornecedorLinhaId(null);
                                setNovoFornecedorNome("");
                              }}
                              className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                              style={{ borderRadius: "var(--radius-button)" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setNovoFornecedorLinhaId(forn.id);
                              setNovoFornecedorNome("");
                            }}
                            className="text-primary hover:text-primary/80 transition-colors"
                            style={{ fontSize: "var(--text-caption)" }}
                          >
                            Criar fornecedor apenas com o nome
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Descrição */}
                  <div className="col-span-12 md:col-span-5">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={forn.descricao}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarFornecedor(forn.id, "descricao", e.target.value)
                      }
                      placeholder="Ex: Pavimento X, Rodapés Y"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  {/* Quantidade */}
                  <div className="col-span-6 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Quantidade
                    </label>
                    <input
                      type="number"
                      value={forn.quantidade === 0 ? "" : forn.quantidade}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarFornecedor(
                          forn.id,
                          "quantidade",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Valor unitário */}
                  <div className="col-span-6 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Valor unit. (€)
                    </label>
                    <input
                      type="number"
                      value={forn.valorUnitario === 0 ? "" : forn.valorUnitario}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        atualizarFornecedor(
                          forn.id,
                          "valorUnitario",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Total */}
                  <div className="col-span-10 md:col-span-2">
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Total (€)
                    </label>
                    <input
                      type="number"
                      value={forn.valor === 0 ? "" : forn.valor}
                      readOnly
                      disabled={isReadOnlyMode}
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Remover */}
                  {!isReadOnlyMode && (
                    <div className="col-span-2 md:col-span-1 flex items-end justify-center">
                      <button
                        onClick={() => removerFornecedor(forn.id)}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                        style={{ borderRadius: "var(--radius)" }}
                        title="Remover"
                      >
                        <Delete20Regular className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Botão Adicionar Fornecedor */}
              {!isReadOnlyMode && (
                <button
                  onClick={adicionarFornecedor}
                  className="w-full px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center justify-center gap-2"
                  style={{
                    borderRadius: "var(--radius-button)",
                    fontSize: "var(--text-base)",
                  }}
                >
                  <Add20Regular className="w-5 h-5" />
                  Adicionar Fornecedor
                </button>
              )}
            </div>

            {/* Equipa Interna */}
            <div className="space-y-3 pt-3 border-t border-border">
              <h4
                className="text-foreground"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Equipa Interna (AJA2)
              </h4>

              {/* Checkbox */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={equipaInterna.ativo}
                  disabled={isReadOnlyMode}
                  onChange={(e) =>
                    setEquipaInterna({ ...equipaInterna, ativo: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                  style={{ borderRadius: "var(--radius-sm)" }}
                />
                <span
                  className="text-foreground"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Execução pela Equipa Interna (AJA2)
                </span>
              </label>

              {/* Campos quando ativo */}
              {equipaInterna.ativo && (
                <div
                  className="p-3 bg-card border border-border space-y-3"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {/* Valor */}
                  <div>
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Valor custo interno (€)
                    </label>
                    <input
                      type="number"
                      value={equipaInterna.valor === 0 ? "" : equipaInterna.valor}
                      disabled={isReadOnlyMode}
                      onChange={(e) =>
                        setEquipaInterna({
                          ...equipaInterna,
                          valor: parseFloat(e.target.value) || 0,
                        })
                      }
                      placeholder="0,00"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      step="0.01"
                    />
                  </div>

                  {/* Notas */}
                  <div>
                    <label
                      className="block text-foreground mb-1"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Notas (opcional)
                    </label>
                    <textarea
                      value={equipaInterna.notas}
                      readOnly={isReadOnlyMode}
                      onChange={(e) =>
                        setEquipaInterna({ ...equipaInterna, notas: e.target.value })
                      }
                      placeholder="Ex: 2 pedreiros durante 5 dias"
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
            style={{
              borderRadius: "var(--radius-button)",
              fontSize: "var(--text-base)",
            }}
          >
            {isReadOnlyMode ? "Fechar" : "Cancelar"}
          </button>
          {!isReadOnlyMode && (
            <button
              onClick={handleSalvar}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2"
              style={{
                borderRadius: "var(--radius-button)",
                fontSize: "var(--text-base)",
                height: "var(--button-height)",
              }}
            >
              Confirmar
            </button>
          )}
        </div>
      </div>
    </>
  );
}
